import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Search, X, Upload, Download, FileSpreadsheet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { getAdminProducts, createProduct, updateProduct, deleteProduct, getCategories, getSubcategories, uploadFile, downloadBulkTemplate, bulkUploadProducts } from '../../services/api';
import { toast } from 'sonner';

const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', category_id: '', subcategory_id: '', description: '',
    short_description: '', price: '', sku: '', stock_status: 'in_stock',
    is_featured: false, is_new_arrival: false, features: '', applications: '', catalog_pdf: ''
  });
  const [catalogFile, setCatalogFile] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFileRef = useRef(null);

  const fetchData = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        getAdminProducts({ search: search || undefined }),
        getCategories()
      ]);
      setProducts(prodRes.data.products);
      setCategories(catRes.data);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSearch = () => {
    setLoading(true);
    fetchData();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '', category_id: '', subcategory_id: '', description: '',
      short_description: '', price: '', sku: '', stock_status: 'in_stock',
      is_featured: false, is_new_arrival: false, features: '', applications: '', catalog_pdf: ''
    });
    setCatalogFile(null);
    setProductImages([]);
    setSubcategories([]);
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      name: product.name || '',
      category_id: product.category_id || '',
      subcategory_id: product.subcategory_id || '',
      description: product.description || '',
      short_description: product.short_description || '',
      price: product.price || '',
      sku: product.sku || '',
      stock_status: product.stock_status || 'in_stock',
      is_featured: product.is_featured || false,
      is_new_arrival: product.is_new_arrival || false,
      features: (product.features || []).join(', '),
      applications: product.applications || '',
      catalog_pdf: product.catalog_pdf || ''
    });
    setCatalogFile(null);
    setProductImages([]);
    if (product.category_id) fetchSubcategories(product.category_id);
    setDialogOpen(true);
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const res = await getSubcategories(categoryId);
      setSubcategories(res.data);
    } catch { setSubcategories([]); }
  };

  const handleCategoryChange = (catId) => {
    setForm({ ...form, category_id: catId, subcategory_id: '' });
    if (catId) fetchSubcategories(catId);
    else setSubcategories([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let catalogPdfUrl = form.catalog_pdf;
      if (catalogFile) {
        const uploadRes = await uploadFile(catalogFile, 'catalog');
        catalogPdfUrl = uploadRes.data.url;
      }
      const payload = {
        ...form,
        price: form.price ? parseFloat(form.price) : null,
        features: form.features ? form.features.split(',').map(f => f.trim()).filter(Boolean) : [],
        catalog_pdf: catalogPdfUrl || null,
      };

      let productId;
      if (editing) {
        await updateProduct(editing.id, payload);
        productId = editing.id;
        toast.success('Product updated');
      } else {
        if (!form.category_id || !form.subcategory_id) {
          toast.error('Category and Subcategory are required');
          return;
        }
        const res = await createProduct(payload);
        productId = res.data.id;
        toast.success('Product created');
      }

      // Upload images if any selected
      if (productImages.length > 0 && productId) {
        const existingImages = editing?.images || [];
        const uploadedImages = [...existingImages];
        const failed = [];
        for (const file of productImages) {
          try {
            const res = await uploadFile(file, 'product');
            if (!res?.data?.url) {
              failed.push(file.name);
              continue;
            }
            uploadedImages.push({ url: res.data.url, alt: file.name, is_primary: uploadedImages.length === 0 });
          } catch (e) {
            failed.push(file.name);
          }
        }
        if (uploadedImages.length > existingImages.length) {
          await updateProduct(productId, { images: uploadedImages });
        }
        if (failed.length) {
          toast.error(`${failed.length} of ${productImages.length} image(s) failed to upload: ${failed.join(', ')}`);
        } else {
          toast.success(`${productImages.length} image(s) uploaded`);
        }
      }

      setDialogOpen(false);
      setCatalogFile(null);
      setProductImages([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await deleteProduct(id);
      toast.success('Product deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleImageUpload = async (productId, file) => {
    try {
      const res = await uploadFile(file, 'product');
      const product = products.find(p => p.id === productId);
      const images = [...(product.images || []), { url: res.data.url, alt: file.name, is_primary: !(product.images?.length) }];
      await updateProduct(productId, { images });
      toast.success('Image uploaded');
      fetchData();
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  // ----- In-dialog gallery operations on the currently-edited product -----
  const removeEditingImage = async (idx) => {
    if (!editing) return;
    if (!window.confirm('Remove this image from the product?')) return;
    const next = (editing.images || []).filter((_, i) => i !== idx);
    // If we removed the primary, promote the first remaining (if any)
    if ((editing.images || [])[idx]?.is_primary && next.length > 0) {
      next[0] = { ...next[0], is_primary: true };
    }
    try {
      await updateProduct(editing.id, { images: next });
      setEditing({ ...editing, images: next });
      toast.success('Image removed');
      fetchData();
    } catch { toast.error('Remove failed'); }
  };

  const setPrimaryEditingImage = async (idx) => {
    if (!editing) return;
    const next = (editing.images || []).map((im, i) => ({ ...im, is_primary: i === idx }));
    try {
      await updateProduct(editing.id, { images: next });
      setEditing({ ...editing, images: next });
      toast.success('Primary image updated');
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const replaceEditingImage = async (idx, file) => {
    if (!editing || !file) return;
    try {
      const up = await uploadFile(file, 'product');
      const next = (editing.images || []).map((im, i) => i === idx ? { ...im, url: up.data.url, alt: file.name } : im);
      await updateProduct(editing.id, { images: next });
      setEditing({ ...editing, images: next });
      toast.success('Image replaced');
      fetchData();
    } catch { toast.error('Replace failed'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadBulkTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'namoh_bulk_product_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const res = await bulkUploadProducts(bulkFile);
      setBulkResult(res.data);
      if (res.data.success > 0) {
        toast.success(`${res.data.success} products added successfully!`);
        fetchData();
      }
      if (res.data.errors?.length > 0) {
        toast.error(`${res.data.errors.length} rows had errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="products-admin">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-500">{products.length} products</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBulkDialogOpen(true)} variant="outline" className="border-[#2E3A6F] text-[#2E3A6F]" data-testid="bulk-upload-btn">
            <FileSpreadsheet size={16} className="mr-2" /> Bulk Upload
          </Button>
          <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-product-btn">
            <Plus size={16} className="mr-2" /> Add Product
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input
            data-testid="product-search"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>Search</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse"><CardContent className="p-4"><div className="h-32 bg-gray-200 rounded" /></CardContent></Card>
          ))}
        </div>
      ) : products.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <p className="text-gray-500">No products found. Add your first product!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <Card key={product.id} className="hover:shadow-md transition-shadow" data-testid={`product-card-${product.id}`}>
              <CardContent className="p-4">
                <div className="relative mb-3">
                  {product.images?.length > 0 ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${product.images[0].url}`} alt={product.name} className="w-full h-36 object-cover rounded-lg" />
                  ) : (
                    <div className="w-full h-36 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">
                      No Image
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {product.is_featured && <span className="text-[10px] px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full font-medium">Featured</span>}
                    {product.is_new_arrival && <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">New</span>}
                  </div>
                </div>
                <h3 className="font-semibold text-sm text-gray-900 truncate">{product.name}</h3>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{product.description}</p>
                {product.price && <p className="text-sm font-bold text-[#2E3A6F] mt-2">Price on request</p>}
                <div className="flex items-center gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(product)} className="flex-1 text-xs">
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs">
                    <Trash2 size={12} />
                  </Button>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleImageUpload(product.id, e.target.files[0])} />
                    <span className="inline-flex items-center justify-center h-8 px-2 rounded-md border text-xs text-gray-600 hover:bg-gray-50">
                      Upload
                    </span>
                  </label>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="product-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category *</Label>
                <select
                  className="w-full h-9 rounded-md border border-input px-3 text-sm"
                  value={form.category_id}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  required={!editing}
                  data-testid="product-category-select"
                >
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Subcategory *</Label>
                <select
                  className="w-full h-9 rounded-md border border-input px-3 text-sm"
                  value={form.subcategory_id}
                  onChange={(e) => setForm({ ...form, subcategory_id: e.target.value })}
                  required={!editing}
                  data-testid="product-subcategory-select"
                >
                  <option value="">Select Subcategory</option>
                  {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={3} />
            </div>
            <div>
              <Label>Product Images — multiple uploads supported</Label>
              <Input type="file" accept="image/*" multiple onChange={(e) => {
                const files = Array.from(e.target.files);
                // Warn if any selected image is too small for crisp display on Retina screens
                files.forEach(f => {
                  const img = new window.Image();
                  img.onload = () => {
                    if (img.naturalWidth < 1200 || img.naturalHeight < 800) {
                      toast.warning(`"${f.name}" is only ${img.naturalWidth}×${img.naturalHeight} px — for sharp display on Retina screens, please upload at least 1600×1200 px.`, { duration: 7000 });
                    }
                  };
                  img.src = URL.createObjectURL(f);
                });
                setProductImages(files);
              }} className="text-sm" data-testid="product-images-upload" />
              <p className="text-[10px] text-gray-500 mt-1">
                <strong>Recommended:</strong> 1600×1200 px or larger (high-quality JPG/PNG, &lt; 2 MB each).
                Smaller images will appear blurry on Retina / high-DPI screens.
              </p>
              {productImages.length > 0 && (
                <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="add-flow-preview-grid">
                  {productImages.map((file, i) => (
                    <div key={`${file.name}-${i}`} className="relative group border rounded bg-gray-50 overflow-hidden">
                      <img src={URL.createObjectURL(file)} alt={file.name} className="w-full h-16 object-contain p-1" />
                      <button
                        type="button"
                        onClick={() => setProductImages(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-0.5 right-0.5 bg-red-600 text-white text-[9px] w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                        title="Remove before saving"
                        data-testid={`add-flow-remove-${i}`}
                      >×</button>
                      <p className="text-[8px] text-gray-600 truncate px-1 pb-0.5" title={file.name}>{file.name}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* In-dialog gallery editor: shows existing images with Make-primary / Replace / Remove actions */}
              {editing?.images?.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {editing.images.map((img, i) => (
                    <div key={img.url || i} className="relative group border rounded-md overflow-hidden bg-gray-50">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt={img.alt} className="w-full h-20 object-contain p-1" data-testid={`gallery-img-${i}`} />
                      {img.is_primary && (
                        <span className="absolute top-1 left-1 text-[9px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">Primary</span>
                      )}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                        {!img.is_primary && (
                          <button type="button" onClick={() => setPrimaryEditingImage(i)} className="text-[10px] text-white bg-yellow-600 hover:bg-yellow-700 px-2 py-0.5 rounded" data-testid={`gallery-set-primary-${i}`}>
                            Make primary
                          </button>
                        )}
                        <label className="text-[10px] text-white bg-blue-600 hover:bg-blue-700 px-2 py-0.5 rounded cursor-pointer" data-testid={`gallery-replace-${i}`}>
                          Replace
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && replaceEditingImage(i, e.target.files[0])} />
                        </label>
                        <button type="button" onClick={() => removeEditingImage(i)} className="text-[10px] text-white bg-red-600 hover:bg-red-700 px-2 py-0.5 rounded" data-testid={`gallery-remove-${i}`}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>SKU</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
              </div>
              <div>
                <Label>Stock Status</Label>
                <select
                  className="w-full h-9 rounded-md border border-input px-3 text-sm"
                  value={form.stock_status}
                  onChange={(e) => setForm({ ...form, stock_status: e.target.value })}
                >
                  <option value="in_stock">In Stock</option>
                  <option value="out_of_stock">Out of Stock</option>
                  <option value="on_request">On Request</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Features (comma separated)</Label>
              <Input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} placeholder="Feature 1, Feature 2" />
            </div>
            <div>
              <Label>Applications</Label>
              <Input value={form.applications} onChange={(e) => setForm({ ...form, applications: e.target.value })} />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} />
                <Label className="text-sm">Featured</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_new_arrival} onCheckedChange={(v) => setForm({ ...form, is_new_arrival: v })} />
                <Label className="text-sm">New Arrival</Label>
              </div>
            </div>
            {/* Catalog PDF Upload - shows when New Arrival is enabled */}
            {form.is_new_arrival && (
              <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                <Label className="text-blue-800 text-xs font-semibold">Product Catalog PDF (for New Arrivals)</Label>
                <Input type="file" accept=".pdf" onChange={(e) => setCatalogFile(e.target.files[0])} className="text-sm" data-testid="product-catalog-upload" />
                {form.catalog_pdf && !catalogFile && (
                  <p className="text-[10px] text-blue-600">Current: {form.catalog_pdf}</p>
                )}
                {catalogFile && (
                  <p className="text-[10px] text-blue-600">Selected: {catalogFile.name}</p>
                )}
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="product-submit-btn">
                {editing ? 'Update' : 'Create'} Product
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(v) => { setBulkDialogOpen(v); if (!v) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet size={20} /> Bulk Product Upload</DialogTitle>
            <DialogDescription>Upload multiple products at once using an Excel file (.xlsx)</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Step 1: Download Template */}
            <div className="bg-[#2E3A6F]/5 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Step 1: Download Template</h4>
              <p className="text-xs text-gray-600">Download the Excel template, fill in your product data, and upload it back.</p>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="text-[#2E3A6F] border-[#2E3A6F]" data-testid="download-template-btn">
                <Download size={14} className="mr-2" /> Download Template (.xlsx)
              </Button>
            </div>

            {/* Step 2: Upload */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Step 2: Upload Filled Excel</h4>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#2E3A6F] transition-colors cursor-pointer"
                onClick={() => bulkFileRef.current?.click()}
                data-testid="bulk-file-drop"
              >
                <Upload className="mx-auto text-gray-400 mb-2" size={24} />
                <p className="text-sm text-gray-600">{bulkFile ? bulkFile.name : 'Click to select Excel file'}</p>
                <p className="text-[10px] text-gray-400 mt-1">Supports .xlsx files only</p>
                <input ref={bulkFileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { setBulkFile(e.target.files[0]); setBulkResult(null); }} />
              </div>
            </div>

            {/* Upload Button */}
            <Button
              className="w-full bg-[#2E3A6F]"
              disabled={!bulkFile || bulkUploading}
              onClick={handleBulkUpload}
              data-testid="bulk-upload-submit"
            >
              {bulkUploading ? 'Uploading...' : `Upload ${bulkFile ? bulkFile.name : 'Products'}`}
            </Button>

            {/* Results */}
            {bulkResult && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total rows:</span>
                  <span className="font-semibold">{bulkResult.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-600">Successful:</span>
                  <span className="font-semibold text-green-700">{bulkResult.success}</span>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-red-600 font-medium text-xs">Errors ({bulkResult.errors.length}):</p>
                    <div className="max-h-32 overflow-y-auto mt-1 space-y-1">
                      {bulkResult.errors.map((err, i) => (
                        <p key={i} className="text-[11px] text-red-500">Row {err.row}: {err.error}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Format Info */}
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer font-medium text-gray-700">Template Format Guide</summary>
              <div className="mt-2 space-y-1 pl-2 border-l-2 border-gray-200">
                <p><strong>name</strong> (required): Product name</p>
                <p><strong>description</strong> (required): Product description</p>
                <p><strong>category_name</strong> (required): Exact category name from CMS</p>
                <p><strong>subcategory_name</strong>: Exact subcategory name</p>
                <p><strong>sku</strong>: Stock keeping unit code</p>
                <p><strong>features</strong>: Separated by | (pipe). E.g. &quot;Feature 1 | Feature 2&quot;</p>
                <p><strong>applications</strong>: Application description text</p>
                <p><strong>is_featured</strong>: TRUE or FALSE</p>
                <p><strong>is_new_arrival</strong>: TRUE or FALSE</p>
                <p><strong>image_url</strong>: Public image URL (https://...)</p>
              </div>
            </details>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsAdmin;
